/**
 * Catalogue access for the planner. Epic 3.1 extends this module (size
 * limits, colours, product matching) rather than adding a second one.
 */
import type { DoorTypeId, ProductRow } from '@planner/config';
import { doorPreviewColour, doorTypeIds, productRowSchema, productsFeed } from '@planner/config';
import { sizeBandContains, type ManufacturedSize } from '@planner/shared/doors';
import { useQuery } from '@tanstack/react-query';
import { apiBaseUrl, region } from './env.js';

type PricedRow = Pick<ProductRow, 'doorType' | 'Active' | 'retailPrice'>;

/**
 * Cheapest active price per door type. Catalogue prices are already
 * GST-inclusive. A type with no usable price is `undefined`, never zero.
 */
export function fromPrices(rows: readonly PricedRow[]): Record<DoorTypeId, number | undefined> {
  const prices = {} as Record<DoorTypeId, number | undefined>;
  for (const type of doorTypeIds) {
    const usable = rows
      .filter((row) => row.doorType === type && row.Active && row.retailPrice > 0)
      .map((row) => row.retailPrice);
    prices[type] = usable.length > 0 ? Math.min(...usable) : undefined;
  }
  return prices;
}

export type DoorPrice = { status: 'estimate' | 'from'; price: number } | { status: 'unavailable' };

function matchingColourPrices(
  rows: readonly ProductRow[],
  type: DoorTypeId,
  size: ManufacturedSize,
): Map<string, Set<number>> {
  const prices = new Map<string, Set<number>>();
  for (const row of rows) {
    if (
      row.Active &&
      row.doorType === type &&
      sizeBandContains(row, size) &&
      Number.isFinite(row.retailPrice) &&
      row.retailPrice > 0
    ) {
      const colourPrices = prices.get(row.frameColour) ?? new Set<number>();
      colourPrices.add(row.retailPrice);
      prices.set(row.frameColour, colourPrices);
    }
  }
  return prices;
}

/** Unambiguous, active prices for each colour at the manufactured size. */
export function colourPricesForSize(
  rows: readonly ProductRow[],
  type: DoorTypeId,
  size: ManufacturedSize,
): ReadonlyMap<string, number> {
  const result = new Map<string, number>();
  for (const [colour, prices] of matchingColourPrices(rows, type, size)) {
    if (prices.size === 1) {
      result.set(colour, Math.min(...prices));
    }
  }
  return result;
}

/** Indicative price for this size, without selecting a colour or SKU. */
export function estimateDoorPrice(
  rows: readonly ProductRow[],
  type: DoorTypeId,
  size: ManufacturedSize,
): DoorPrice {
  const prices = matchingColourPrices(rows, type, size);
  const previewPrices = prices.get(doorPreviewColour);
  if (previewPrices && previewPrices.size > 1) {
    return { status: 'unavailable' };
  }
  if (previewPrices?.size === 1) {
    return { status: 'estimate', price: Math.min(...previewPrices) };
  }
  if ([...prices.values()].some((colourPrices) => colourPrices.size > 1) || prices.size === 0) {
    return { status: 'unavailable' };
  }
  return {
    status: 'from',
    price: Math.min(...[...prices.values()].flatMap((values) => [...values])),
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Validate the normalized API response without coercing malformed bands. */
export function parseProductData(value: unknown, regionCode: string): ProductRow[] {
  if (!isRecord(value) || value.feed !== productsFeed.name || value.region !== regionCode) {
    throw new Error('Product feed or region is invalid');
  }
  if (
    typeof value.syncedAt !== 'string' ||
    !Number.isFinite(Date.parse(value.syncedAt)) ||
    !Number.isSafeInteger(value.rowCount) ||
    !Array.isArray(value.rows) ||
    value.rowCount !== value.rows.length
  ) {
    throw new Error('Product feed envelope is invalid');
  }

  return value.rows.map((row: unknown, index: number) => {
    if (
      !isRecord(row) ||
      typeof row.Active !== 'boolean' ||
      typeof row.itemStatus !== 'string' ||
      row.Active !== (row.itemStatus === 'Active') ||
      typeof row.retailPrice !== 'number' ||
      !Number.isFinite(row.retailPrice) ||
      !isRecord(row.metaData) ||
      // Inactive rows may have zero bands; only active rows establish availability.
      !['minDoorWidth', 'maxDoorWidth', 'minDoorHeight', 'maxDoorHeight'].every(
        (key) =>
          typeof row[key] === 'number' &&
          Number.isSafeInteger(row[key]) &&
          (!row.Active || row[key] > 0),
      )
    ) {
      throw new Error(`Product feed row ${index + 1} is invalid`);
    }
    const parsed = productRowSchema.safeParse(row);
    if (
      !parsed.success ||
      parsed.data.doorType !== row.doorType ||
      parsed.data.inventoryID !== row.inventoryID ||
      parsed.data.description !== row.description ||
      parsed.data.frameColour !== row.frameColour
    ) {
      throw new Error(`Product feed row ${index + 1} is invalid`);
    }
    return parsed.data;
  });
}

async function fetchProducts(): Promise<ProductRow[]> {
  const response = await fetch(
    `${apiBaseUrl}/products/${encodeURIComponent(region.code)}/products`,
  );
  if (!response.ok) {
    throw new Error(`Could not load products (${response.status})`);
  }
  const body: unknown = await response.json();
  return parseProductData(body, region.code);
}

/** The active region's product catalogue. */
export function useProducts() {
  return useQuery({
    queryKey: ['products', region.code],
    queryFn: fetchProducts,
    // Avoid flashing back to "loading" when a customer returns to a Step.
    staleTime: 5 * 60 * 1000,
  });
}
